import { Box, Card, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

export interface SingleSelectInfo {
  options: MultiSelectOption[];
  header: string;
  description: string;
  styleInfo: MultiSelectStyleInfo;
  selectionChangeHandler: (damageCode: string[]) => void;
}

export interface MultiSelectOption {
  isSelected: boolean;
  text: string;
  id: string;
}

export interface MultiSelectStyleInfo {
  headerStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: number;
    color: string;
    lineHeight: string;
  };
  descriptionStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: number;
    color: string;
    lineHeight: string;
  };
  optionStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: number;
    color: string;
    lineHeight: string;
    checkBoxBorderColor: string;
    checkBoxSelectedColor: string;
    borderColor?: string;
    selectedBorderColor?: string;
    borderRadius?: string;
    checkBoxSize?: string;
    optionHeight?: string;
  };
}
export default function MultiSelect(props: SingleSelectInfo) {
  /*High level logic*/
  /*
  Two states are maintained.
  1) 'selectedOptionIds' to track the id of selection. 
  No default value during init. Any change to this state will trigger a handler in parent 
  via 'useEffect' hook.
  UI interation can trigger the state change. If there is a value that was selected earlier, 
  function 'syncSelectedIdsInfo' is called once for state sync during page load, which essentially 
  updates 'selectedOptionIds' state.
  2) 'options' to maintain state that is used for UI rendering. 
  Default value for init will be passed via props. UI interation can trigger the state change. 
  A selection\unselection event will update both 'selectedOptionIds' and 'options' states.
  */
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [options, setOptions] = useState<MultiSelectOption[]>(props.options);

  // Update local state with selected 'option Ids'
  const updateLocalStateOnSelectedOptionIds = useCallback(
    (selectedId: string, isSelected: boolean) => {
      if (isSelected && !selectedOptionIds.includes(selectedId)) {
        // Add the Id to collection
        setSelectedOptionIds([...selectedOptionIds, selectedId]);
      }
      if (!isSelected && selectedOptionIds.includes(selectedId)) {
        // Remove the Id from collection
        setSelectedOptionIds((currentSelectedOptionIds) =>
          currentSelectedOptionIds.filter((item) => item !== selectedId)
        );
      }
    },
    [selectedOptionIds]
  );
  // This is executed only once after initial rendering
  useEffect(() => {
    setOptions(props.options);
    function syncSelectedIdsInfo(options: MultiSelectOption[]) {
      options.forEach((option) => {
        if (option.isSelected) {
          updateLocalStateOnSelectedOptionIds(option.id, option.isSelected);
        }
      });
    }
    syncSelectedIdsInfo(props.options);
  }, [props.options, updateLocalStateOnSelectedOptionIds]);

  // This is be executed when the state changes
  useEffect(() => {
    props.selectionChangeHandler(selectedOptionIds);
  }, [props, selectedOptionIds]);

  function updateLocalStateOnOptions(selectedId: string, isSelected: boolean) {
    const updatedOptions = options.map((option) => {
      if (option.id === selectedId && isSelected) {
        option.isSelected = true;
      }
      if (option.id === selectedId && !isSelected) {
        option.isSelected = false;
      }
      return option;
    });
    setOptions(updatedOptions);
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Update local state with selected 'option Ids'
    const selectedId = (event.target as HTMLInputElement).name;
    const isSelected = (event.target as HTMLInputElement).checked;
    updateLocalStateOnSelectedOptionIds(selectedId, isSelected);
    // Update the local state of 'options' to reflect the 'checked' or 'unchecked' state of check box
    updateLocalStateOnOptions(selectedId, isSelected);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography gutterBottom sx={{ ...props.styleInfo.headerStyle, marginBottom: '1.5rem' }}>
        {props.header}
      </Typography>
      {props.description && (
        <Typography sx={{ ...props.styleInfo.descriptionStyle, marginBottom: '1.5rem' }} gutterBottom>
          {props.description}
        </Typography>
      )}
      <FormGroup>
        {options &&
          options.map((option, index) => (
            <Card
              sx={{
                mx: '4px',
                px: '15px',
                marginTop: '.5rem',
                boxShadow: 'none',
                paddingTop: '.5rem',
                paddingBottom: '.5rem',
                border: selectedOptionIds.includes(option.id)
                  ? props.styleInfo.optionStyle.selectedBorderColor
                  : props.styleInfo.optionStyle.borderColor,
                borderRadius: props.styleInfo.optionStyle.borderRadius,
                height: props.styleInfo.optionStyle.optionHeight,
                justifyContent: 'center',
                display: 'flex',
              }}
              // variant={'outlined'} //TODO: Need to change dynamically for other opcos
              key={option.id}
            >
              <FormControlLabel
                label={<Typography sx={{ ...props.styleInfo.optionStyle }}>{option.text}</Typography>}
                control={
                  <Checkbox
                    checked={option.isSelected}
                    onChange={handleChange}
                    name={option.id}
                    disableRipple
                    sx={{
                      paddingTop: 0,
                      paddingBottom: 0,
                      color: props.styleInfo.optionStyle.checkBoxBorderColor,
                      '&.Mui-checked': {
                        color: props.styleInfo.optionStyle.checkBoxSelectedColor,
                      },
                      '& svg': {
                        width: props.styleInfo.optionStyle.checkBoxSize,
                        height: props.styleInfo.optionStyle.checkBoxSize,
                      },
                    }}
                  />
                }
                key={option.id}
                tabIndex={index + 1}
                sx={{ width: '100%' }}
              />
            </Card>
          ))}
      </FormGroup>
    </Box>
  );
}
